import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Alert,
  Button,
  Capacity,
  Card,
  Input,
  Select,
  StatusBadge,
  Tag,
  Textarea,
} from '@rallyshift/design-system'
import api from '../../api/client'
import { CAPACITY_LABELS, statusLabel } from '../../utils/status'

interface DayShift {
  id: string
  title: string
  status: string
  start_time: string
}

/** yyyy-MM-dd and HH:mm in the browser's zone, which is the zone organisers work in. */
function splitLocal(iso: string): { date: string; time: string } {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  }
}

function toIso(date: string, time: string): string {
  return new Date(`${date}T${time}`).toISOString()
}

/**
 * The shift builder as a full page (design screen 1e), with a live preview of what the
 * volunteer will see. It doubles as the edit form, since the two are the same fields.
 */
const ShiftBuilderPage: React.FC = () => {
  const navigate = useNavigate()
  const { shiftId } = useParams<{ shiftId: string }>()
  const editing = Boolean(shiftId)

  const today = splitLocal(new Date().toISOString()).date

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [date, setDate] = useState(today)
  const [from, setFrom] = useState('16:00')
  const [to, setTo] = useState('20:00')
  const [location, setLocation] = useState('')
  const [maxVolunteers, setMaxVolunteers] = useState(1)
  const [eventId, setEventId] = useState('')
  const [requiredSkills, setRequiredSkills] = useState<string[]>([])
  const [shiftType, setShiftType] = useState<'public' | 'private'>('public')

  const [catalogue, setCatalogue] = useState<string[]>([])
  const [events, setEvents] = useState<{ id: string; title: string }[]>([])
  const [dayShifts, setDayShifts] = useState<DayShift[]>([])

  const [loading, setLoading] = useState(editing)
  const [saving, setSaving] = useState<'draft' | 'publish' | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    Promise.all([
      api.organizations.listSkills().catch(() => []),
      api.events.list().catch(() => []),
    ]).then(([skills, eventList]) => {
      setCatalogue(skills.filter((s: any) => !s.archived).map((s: any) => s.name))
      setEvents((eventList as any[]).map((e) => ({ id: e.id, title: e.title })))
    })
  }, [])

  useEffect(() => {
    if (!shiftId) return
    api.shifts
      .get(shiftId)
      .then((shift) => {
        const start = splitLocal(shift.start_time)
        const end = splitLocal(shift.end_time)
        setTitle(shift.title)
        setDescription(shift.description ?? '')
        setDate(start.date)
        setFrom(start.time)
        setTo(end.time)
        setLocation(shift.location ?? '')
        setMaxVolunteers(shift.max_volunteers ?? 1)
        setEventId(shift.event_id ?? '')
        setRequiredSkills(shift.required_skills ?? [])
        setShiftType(shift.shift_type ?? 'public')
      })
      .catch((e) => setError(e.message || 'Deze dienst kon niet worden geladen.'))
      .finally(() => setLoading(false))
  }, [shiftId])

  const loadDay = useCallback(async () => {
    try {
      const all = await api.shifts.list()
      setDayShifts(
        all
          .filter((s: any) => splitLocal(s.start_time).date === date && s.id !== shiftId)
          .sort((a: any, b: any) => a.start_time.localeCompare(b.start_time)),
      )
    } catch {
      setDayShifts([])
    }
  }, [date, shiftId])

  useEffect(() => {
    loadDay()
  }, [loadDay])

  const unselectedSkills = useMemo(
    () => catalogue.filter((s) => !requiredSkills.includes(s)),
    [catalogue, requiredSkills],
  )

  const valid = title.trim() && location.trim() && date && from && to && to > from

  const submit = async (status: 'draft' | 'active') => {
    if (!valid) {
      setError('Vul een titel, locatie en een geldige begin- en eindtijd in.')
      return
    }
    setSaving(status === 'draft' ? 'draft' : 'publish')
    setError('')
    try {
      const payload = {
        event_id: eventId || null,
        title: title.trim(),
        description: description.trim() || null,
        start_time: toIso(date, from),
        end_time: toIso(date, to),
        location: location.trim(),
        max_volunteers: maxVolunteers,
        shift_type: shiftType,
        required_skills: requiredSkills,
        status,
      }
      if (shiftId) {
        await api.shifts.update(shiftId, payload)
      } else {
        await api.shifts.create(payload)
      }
      navigate('/shifts')
    } catch (e: any) {
      setError(e.message || 'Opslaan is niet gelukt.')
    } finally {
      setSaving(null)
    }
  }

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-rs-spin rounded-full border-2.5 border-action-primary border-r-transparent" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <h1 className="font-display text-display-lg text-text-primary">
        {editing ? 'Dienst bewerken' : 'Nieuwe dienst'}
      </h1>

      {error && (
        <Alert variant="danger" onDismiss={() => setError('')}>
          {error}
        </Alert>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.3fr_1fr]">
        <Card>
          <div className="space-y-5">
            <Input label="Titel" value={title} onChange={(e) => setTitle(e.target.value)} />

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Input
                label="Datum"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
              <Input label="Van" type="time" value={from} onChange={(e) => setFrom(e.target.value)} />
              <Input label="Tot" type="time" value={to} onChange={(e) => setTo(e.target.value)} />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                label="Locatie"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
              />
              <Input
                label="Max. vrijwilligers"
                type="number"
                min={1}
                value={maxVolunteers}
                onChange={(e) => setMaxVolunteers(Math.max(1, Number(e.target.value) || 1))}
              />
            </div>

            {events.length > 0 && (
              <Select
                label="Evenement"
                value={eventId}
                onChange={(e) => setEventId(e.target.value)}
                options={[
                  { value: '', label: 'Geen evenement' },
                  ...events.map((ev) => ({ value: ev.id, label: ev.title })),
                ]}
              />
            )}

            <Textarea
              label="Omschrijving"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Wat gaat de vrijwilliger doen?"
            />

            <div>
              <p className="mb-2 text-sm font-semibold text-text-primary">
                Vereiste vaardigheden
              </p>
              <div className="flex flex-wrap gap-2">
                {requiredSkills.map((skill) => (
                  <Tag
                    key={skill}
                    selected
                    removeLabel={`Verwijder ${skill}`}
                    onRemove={() =>
                      setRequiredSkills((prev) => prev.filter((s) => s !== skill))
                    }
                  >
                    {skill}
                  </Tag>
                ))}
                {unselectedSkills.map((skill) => (
                  <Tag key={skill} onClick={() => setRequiredSkills((prev) => [...prev, skill])}>
                    + {skill}
                  </Tag>
                ))}
                {catalogue.length === 0 && (
                  <p className="text-sm text-text-muted">
                    Nog geen vaardigheden ingesteld. Voeg ze toe bij Instellingen.
                  </p>
                )}
              </div>
            </div>

            <div>
              <p className="text-sm font-semibold text-text-primary">Zichtbaarheid</p>
              <p className="mb-2 text-[13px] text-text-muted">
                Besloten diensten zijn alleen op uitnodiging
              </p>
              <div className="flex gap-2">
                <button type="button" onClick={() => setShiftType('public')}>
                  <StatusBadge
                    dimension="visibility"
                    status="public"
                    label="openbaar"
                    className={shiftType === 'public' ? '' : 'opacity-50'}
                  />
                </button>
                <button type="button" onClick={() => setShiftType('private')}>
                  <StatusBadge
                    dimension="visibility"
                    status="private"
                    label="besloten"
                    className={shiftType === 'private' ? '' : 'opacity-50'}
                  />
                </button>
              </div>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap justify-end gap-2">
            <Button variant="ghost" onClick={() => navigate('/shifts')}>
              Annuleren
            </Button>
            <Button
              variant="secondary"
              onClick={() => submit('draft')}
              loading={saving === 'draft'}
            >
              Bewaar als concept
            </Button>
            <Button onClick={() => submit('active')} loading={saving === 'publish'}>
              Publiceer dienst
            </Button>
          </div>
        </Card>

        <div className="space-y-6">
          <div>
            <p className="mb-2 text-sm font-semibold text-text-muted">
              Zo ziet de vrijwilliger het
            </p>
            <Card>
              <h3 className="font-display text-display-sm text-text-primary">
                {title || 'Naamloze dienst'}
              </h3>
              <p className="mt-1 text-md text-text-secondary">
                {from}–{to}
              </p>
              <p className="text-sm text-text-muted">{location || 'Nog geen locatie'}</p>

              {requiredSkills.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {requiredSkills.map((skill) => (
                    <Tag key={skill}>{skill}</Tag>
                  ))}
                </div>
              )}

              <div className="mt-4">
                <Capacity value={0} max={maxVolunteers} stateLabels={CAPACITY_LABELS} />
              </div>

              <div className="mt-4">
                <Button variant="tenant" size="lg" fullWidth>
                  Ik help mee
                </Button>
              </div>
            </Card>
          </div>

          <div>
            <p className="mb-2 text-sm font-semibold text-text-muted">Deze dag</p>
            <Card>
              {dayShifts.length === 0 ? (
                <p className="text-sm text-text-muted">
                  Er staat nog niets anders gepland op deze dag.
                </p>
              ) : (
                <ul className="divide-y divide-border">
                  {dayShifts.map((shift) => (
                    <li key={shift.id} className="flex items-center justify-between gap-3 py-2">
                      <span className="truncate text-sm text-text-primary">{shift.title}</span>
                      <StatusBadge
                        dimension="shift"
                        status={shift.status}
                        size="sm"
                        label={statusLabel('shift', shift.status)}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ShiftBuilderPage
